import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useMediaQuery } from 'react-responsive'
import { motion, AnimatePresence } from 'framer-motion'
import { questions, results } from './data/questions.json'

import Container from './components/container'
import { Text, Heading } from './components/textElements'
import AnswerButton from './components/answerButton'
import DecorativeHeading from './components/decorativeHeading'
import { BackgroundElements, CloudSky, Branches } from './components/backgroundElements'
import Masthead from './components/masthead'
import DecorativeInput from './components/decorativeInput'
import DecorativeButton from './components/decorativeButton'
import Question from './components/question'
import Form from './components/form'
import MessageForm from './components/messageForm'

import forefrontLogo from './../img/forefront.png'
import { devicesMax } from './styles/breakpoints.js'

import {
  StyledBaseView,
  Frame,
  Logo,
  Section,
  IntroLine,
  Pagination,
  PaginationItem,
  CaptionArea,
  AnswersGrid,
  CaptionAffix,
} from './styles'

const Motion = props => <motion.div {...props} />
const MotionText = styled(motion.span)`
  display: inline-block;
`

const sectionExit = {
  opacity: 0,
  transition: { duration: 0.5 },
}

const parent = {
  visible: {
    transition: {
      delayChildren: 0.25,
      staggerChildren: 0.075,
    },
  },
  exit: {
    transition: {
      delayChildren: 0.25,
      staggerChildren: 0.05,
    },
  },
}

const child = {
  hidden: {
    opacity: 0,
    y: -50,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      y: { type: 'spring', stiffness: 50, mass: 0.2, damping: 15 },
    },
  },
  exit: {
    opacity: 0,
    y: 50,
    transition: {
      y: { type: 'spring', stiffness: 150, mass: 2, damping: 10 },
    },
  },
}

const AnswersList = ({ name, question, answers, onClick }) => (
  <Motion
    initial="hidden"
    animate="visible"
    variants={parent}
    exit={{ opacity: 0, transition: { duration: 0.5 } }}
  >
    <Question>
      {question.map((paragraph, index) => (
        <Motion key={index} variants={child}>
          <Text marginBottom="2rem">{paragraph.replace(/{name}/g, name)}</Text>
        </Motion>
      ))}
    </Question>
    <AnswersGrid columns={2} gap="1.5rem">
      {answers.map((answer, index) => (
        <Motion key={index} variants={child}>
          <AnswerButton {...answer} onClick={() => onClick(index)} />
        </Motion>
      ))}
    </AnswersGrid>
  </Motion>
)

const BaseView = () => {
  const [animation, setAnimation] = useState({
    introLine: 'visible',
    introContent: 'hidden',
  })
  const [currentStep, setCurrentStep] = useState(0)
  const [currentQuestion, setCurrentQuestion] = useState(0)

  const [error, setError] = useState('')
  const [name, setName] = useState('')
  const [answers, setAnswers] = useState([])
  const [selections, setSelections] = useState([])
  const [score, setScore] = useState({})
  const [caption, setCaption] = useState('')
  const [result, setResult] = useState('')
  const [formValues, setFormValues] = useState({})
  const isMaxTabletSm = useMediaQuery({ query: devicesMax.mobile.lg })

  const moveToNextStep = () => setCurrentStep(currentStep + 1)

  const answerQuestion = index => {
    setAnswers([...answers, index])
    setSelections({ ...selections, [currentQuestion]: index })

    if (currentQuestion >= questions.length - 1) setCurrentStep(currentStep + 1)
    else setCurrentQuestion(currentQuestion + 1)
  }

  const onShareClick = () => {
    FB.ui(
      {
        method: 'share',
        href: `http://forefront.international/goodtimes/goneby/${result.encoded}.html`,
        quote: `In 2020, I would like to ${caption}`,
        hashtag: '#ForeGoodTimes',
      },
      function(resp) {
        if (resp) {
          moveToNextStep()
          setError('')
        } else setError('Please share you message to be in the running for the giveaway.')
      }
    )
  }

  const getResults = () =>
    results.find(result => {
      const condition = result.condition.split(' - ').map(num => parseInt(num))

      return (!condition[1] && score === condition[0]) || (score >= condition[0] && score <= condition[1])
    })

  useEffect(() => {
    if (Object.keys(selections).length)
      setScore(
        questions.length -
          Object.values(selections).reduce((total, current) => parseInt(total) + parseInt(current))
      )
  }, [selections])

  useEffect(() => {
    setResult(getResults)
  }, [score])

  return (
    <StyledBaseView>
      <BackgroundElements>
        <AnimatePresence>
          {animation.introLine === 'exit' && currentStep === 0 && <CloudSky />}
          {!isMaxTabletSm && currentStep !== 0 && currentStep !== 4 && <Branches />}
        </AnimatePresence>
      </BackgroundElements>

      <Frame>
        <a href="/goodtimes">
          <Logo src={forefrontLogo} />
        </a>
      </Frame>

      <AnimatePresence exitBeforeEnter={true}>
        {currentStep === 0 && (
          <Section key={0} exit={sectionExit}>
            <IntroLine>
              <Heading>
                <Motion
                  initial="hidden"
                  animate={animation.introLine}
                  variants={parent}
                  onAnimationStart={() => {
                    if (animation.introLine === 'exit') {
                      setAnimation({ ...animation, introContent: 'visible' })
                    }
                  }}
                  onAnimationComplete={() => {
                    setAnimation({ ...animation, introLine: 'exit' })
                  }}
                >
                  {'TO GOOD TIMES GONE BY'.split(' ').map((word, index) => (
                    <MotionText key={index} variants={child}>
                      {word}&nbsp;
                    </MotionText>
                  ))}
                </Motion>
              </Heading>
            </IntroLine>

            <Container sizing="small">
              <Motion initial="hidden" animate={animation.introContent} variants={parent}>
                <Motion variants={child}>
                  <DecorativeHeading>TO GOOD TIMES GONE BY</DecorativeHeading>
                </Motion>
                <Motion variants={child}>
                  <Masthead>
                    <Text marginBottom="1.5rem" size="1.25rem" fontStyle="italic">
                      “The past remembered
                      <br />
                      is a good guide for the future”
                    </Text>
                    <Text marginBottom="1.5rem" size="1.25rem" letterSpacing="5px">
                      明镜所以照形, 古事所以知今
                    </Text>
                  </Masthead>
                </Motion>
                <Motion variants={child}>
                  <Text>
                    As we bid farewell to the old year, we welcome a new decade and a new zodiac cycle,
                    ushering in a new season of prosperity.
                  </Text>
                  <Text>Before we begin, please tell us your name.</Text>
                </Motion>
                <Motion variants={child}>
                  <DecorativeInput value={name} onChange={e => setName(e.target.value)} />
                </Motion>
                <Motion variants={child}>
                  <DecorativeButton
                    onClick={() => {
                      if (!name) setError('Please enter your name.')
                      else {
                        moveToNextStep()
                        setError('')
                      }
                    }}
                  >
                    Continue
                  </DecorativeButton>
                  <Text>{error}</Text>
                </Motion>
              </Motion>
            </Container>
          </Section>
        )}

        {currentStep === 1 && (
          <Section key={1} exit={sectionExit}>
            <Container sizing="small">
              <Motion initial="hidden" animate="visible" variants={parent}>
                <Motion variants={child}>
                  <Question>
                    <Text>
                      {name}, the Lunar New Year is the season of renewal – when the dark of Winter gives way
                      to the blossoming of Spring.
                    </Text>
                  </Question>
                </Motion>
                <Motion variants={child}>
                  <DecorativeButton onClick={() => moveToNextStep()}>Continue</DecorativeButton>
                </Motion>
              </Motion>
            </Container>
          </Section>
        )}

        {currentStep === 2 && (
          <Section style={{ padding: '2rem 0 4rem' }} key={2} exit={sectionExit}>
            <Container sizing="small">
              <AnimatePresence exitBeforeEnter={true}>
                {questions.map(
                  ({ question, answers }, index) =>
                    currentQuestion === index && (
                      <AnswersList
                        key={index}
                        name={name}
                        question={question}
                        answers={answers}
                        onClick={answerQuestion}
                      />
                    )
                )}
              </AnimatePresence>
            </Container>

            <Pagination animate={{ opacity: [0, 1] }} transition={{ delay: 0.75, duration: 0.5 }}>
              {questions.map((question, index) => (
                <PaginationItem
                  key={index}
                  isCompleted={index < currentQuestion}
                  isCurrent={index === currentQuestion}
                  onClick={() => index < currentQuestion && setCurrentQuestion(index)}
                >
                  <Text>{question.character}</Text>
                </PaginationItem>
              ))}
            </Pagination>
          </Section>
        )}

        {currentStep === 3 && (
          <Section key={3} exit={sectionExit}>
            <Container sizing="small">
              <Motion initial="hidden" animate="visible" variants={parent}>
                <Motion variants={child}>
                  <Question>
                    <Text marginBottom="1rem">
                      Our past shapes who we are now, but it doesn’t define who we choose to be.
                    </Text>
                    <Text>
                      Use your good memories of the past to build fresh beginnings. Big or small, what’s
                      something you’re determined to achieve this year?
                    </Text>
                  </Question>
                </Motion>
                <Motion variants={child} style={{ position: 'relative' }}>
                  <CaptionAffix>In 2020 I would like to</CaptionAffix>
                  <CaptionArea rows="4" value={caption} onChange={e => setCaption(e.target.value)} />
                </Motion>
                <Motion variants={child}>
                  <DecorativeButton
                    onClick={() => {
                      if (!caption) setError('Please enter what you hope to achieve.')
                      else moveToNextStep()
                    }}
                  >
                    Continue
                  </DecorativeButton>
                  <Text>{error}</Text>
                </Motion>
              </Motion>
            </Container>
          </Section>
        )}

        {currentStep === 4 && (
          <Section key={4} exit={sectionExit}>
            <Container sizing="large">
              <Motion initial="hidden" animate="visible" variants={parent}>
                <Motion variants={child}>
                  <Question>
                    <Text>To enter our Ang Pow Giveaway, please fill in the form below.</Text>
                  </Question>
                </Motion>
                <Motion variants={child}>
                  <Form
                    getValues={setFormValues}
                    caption={caption}
                    onSuccess={values => {
                      setFormValues(values)
                      moveToNextStep()
                    }}
                  />
                </Motion>
              </Motion>
            </Container>
          </Section>
        )}

        {currentStep === 5 && (
          <Section key={5} exit={sectionExit}>
            <Container sizing="small">
              <Motion initial="hidden" animate="visible" variants={parent}>
                <Motion variants={child}>
                  <Question>
                    <Text>Thank you, {name}, for joining us on this trip down memory lane.</Text>
                  </Question>
                </Motion>
                <Motion variants={child}>
                  <Masthead>
                    <Text size="1.35rem" margin="2rem 0">
                      {result && result.message}
                    </Text>
                  </Masthead>
                </Motion>
                <Motion variants={child}>
                  <Text marginTop="1.5rem">
                    Share this on Facebook with the hashtag <strong>#ForeGoodTimes</strong> to enter our
                    FOREFRONT Ang Pow Giveaway!
                  </Text>
                </Motion>
                <Motion variants={child}>
                  <Text>Remember to set your profile and post to public.</Text>
                </Motion>
                <Motion variants={child}>
                  <DecorativeButton onClick={() => onShareClick(caption)}>Share result</DecorativeButton>
                  <Text>{error}</Text>
                </Motion>
              </Motion>
            </Container>
          </Section>
        )}

        {currentStep === 6 && (
          <Section key={6} exit={sectionExit}>
            <Container sizing="large">
              <Motion initial="hidden" animate="visible" variants={parent}>
                <Motion variants={child}>
                  <DecorativeHeading>A BLESSING FOR THE FUTURE</DecorativeHeading>
                </Motion>
                <Motion variants={child}>
                  <Text>
                    We’ve looked at the past, now it’s time to move forward into the future. Take a moment to
                    consider your dreams and aspirations for the coming year and continue this journey by
                    writing a letter to your future self.
                  </Text>
                  <Text>Here are some examples to guide you along:</Text>
                </Motion>
                <Motion variants={child}>
                  <MessageForm personalDetails={formValues} onSuccess={moveToNextStep} />
                </Motion>
              </Motion>
            </Container>
          </Section>
        )}

        {currentStep === 7 && (
          <Section key={7} exit={sectionExit}>
            <Container sizing="small">
              <Motion initial="hidden" animate="visible" variants={parent}>
                <Motion variants={child}>
                  <DecorativeHeading>THANK YOU FOR SUBMITTING!</DecorativeHeading>
                </Motion>
                <Motion variants={child}>
                  <Masthead>
                    <Text marginBottom="1.5rem" size="1.25rem" fontStyle="italic">
                      “A journey of a thousand miles
                      <br />
                      begins with the first step”
                    </Text>
                    <Text marginBottom="1.5rem" size="1.25rem" letterSpacing="5px">
                      千里之行，始於足下
                    </Text>
                  </Masthead>
                </Motion>
                <Motion variants={child}>
                  <Text marginBottom="2rem">
                    With these words, may you step into new beginnings with renewed purpose and high hopes.
                  </Text>
                  <DecorativeButton>
                    <a href="/goodtimes">BACK TO HOME</a>
                  </DecorativeButton>
                </Motion>
              </Motion>
            </Container>
          </Section>
        )}
      </AnimatePresence>
    </StyledBaseView>
  )
}

export default BaseView
